import { Children, cloneElement, isValidElement, useId, useState, type LabelHTMLAttributes, type ReactElement, type ReactNode } from "react";
import "./fieldLabel.css";

/** One visual/accessibility convention over existing required-field rules. */
export function RequiredMark() {
  return <span className="required-mark" aria-hidden="true"> *</span>;
}

function elements(children: ReactNode, predicate: (element: ReactElement<Record<string, unknown>>) => boolean): boolean {
  return Children.toArray(children).some(child => isValidElement<Record<string, unknown>>(child) && (predicate(child) || elements(child.props.children as ReactNode, predicate)));
}

function inputLike(child: ReactElement<Record<string, unknown>>): boolean {
  return typeof child.type === "string" ? ["input", "select", "textarea"].includes(child.type) : "required" in child.props || "inputMode" in child.props;
}

export function FieldLabel({ children, required, error, onInvalidCapture, onChangeCapture, ...props }: LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean; error?: string | null }) {
  const id = useId();
  const [invalid, setInvalid] = useState<string | null>(null);
  const hasMark = elements(children, child => child.type === RequiredMark || typeof child.props.className === "string" && child.props.className.split(/\s+/).includes("required"));
  const nativeRequired = elements(children, child => !!child.props.required);
  const mandatory = required === true || nativeRequired || hasMark;
  const message = error || invalid;
  const childList = Children.toArray(children);
  const firstControl = childList.findIndex(child => isValidElement<Record<string, unknown>>(child) && (inputLike(child) || elements(child, inputLike)));
  const hasLabelBefore = childList.slice(0, firstControl).some(child => typeof child === "string" ? child.trim().length > 0 : child !== null);
  const addSemantics = (nodes: ReactNode): ReactNode => Children.map(nodes, child => {
    if (!isValidElement<Record<string, unknown>>(child)) return child;
    const control = typeof child.type === "string" && ["input", "select", "textarea"].includes(child.type);
    const values: Record<string, unknown> = {};
    if (typeof child.props.className === "string" && child.props.className.split(/\s+/).includes("required")) values["aria-hidden"] = true;
    if (control && (child.props.required || mandatory && !nativeRequired)) {
      values.required = true;
      values["aria-required"] = true;
    }
    if (control && message) {
      values["aria-invalid"] = true;
      values["aria-describedby"] = [child.props["aria-describedby"], `${id}-error`].filter(Boolean).join(" ");
    }
    if (child.props.children) values.children = addSemantics(child.props.children as ReactNode);
    return cloneElement(child, values);
  });
  const content = addSemantics(children);
  const marked = Children.toArray(content);
  // Keep the caption and marker in one layout item. A label is often a
  // flex-column; a naked marker sibling would otherwise occupy its own row.
  if (mandatory && hasLabelBefore && firstControl > 0) {
    const caption = marked.splice(0, firstControl);
    if (!hasMark) caption.push(<RequiredMark key="required" />);
    marked.unshift(<span key="caption" className="field-label-caption">{caption}</span>);
  } else if (mandatory && firstControl < 0) {
    if (!hasMark) marked.push(<RequiredMark key="required" />);
    marked.splice(0, marked.length, <span key="caption" className="field-label-caption">{[...marked]}</span>);
  } else if (mandatory && firstControl >= 0 && marked.length > firstControl + 1) {
    const caption = marked.splice(firstControl + 1);
    if (!hasMark) caption.push(<RequiredMark key="required" />);
    marked.push(<span key="caption" className="field-label-caption">{caption}</span>);
  } else if (mandatory && !hasMark) marked.push(<RequiredMark key="required" />);
  return <label {...props} onInvalidCapture={event => {
    const control = event.target as HTMLInputElement;
    setInvalid(control.validationMessage || "Complete this required field.");
    onInvalidCapture?.(event);
  }} onChangeCapture={event => {
    const control = event.target as HTMLInputElement;
    if (control.validity?.valid) setInvalid(null);
    onChangeCapture?.(event);
  }}>{marked}{message && <span id={`${id}-error`} className="field-error" role="alert">{message}</span>}</label>;
}

/** Button-driven editors can use the same native rules as a submitted form. */
export function validateRequiredFields(container: ParentNode | null): boolean {
  const controls = container?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input,select,textarea");
  if (!controls) return true;
  for (const control of controls) {
    if (control.disabled || !control.willValidate || control.validity.valid) continue;
    control.reportValidity();
    control.focus();
    return false;
  }
  return true;
}
